import { useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { RichTextCheckbox } from '@/schemas/sceneDataSchema';
import { encodeFieldKey } from '@/schemas/validation/ValidationSchemaGenerator';
import { Checkbox, FormControl, FormControlLabel, FormHelperText } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import useAnalytics from '@/hooks/use-analytics';
import { useFieldBinding, seedFormFromStore } from '@/hooks/use-field-binding';
import { RichText } from '../../RichText';

export default function RichTextCheckboxFormElement({
  form,
  component,
}: {
  form: UseFormReturn;
  component: RichTextCheckbox;
}) {
  const { trackEvent } = useAnalytics();
  const fieldName = encodeFieldKey(component);
  const binding = useFieldBinding(component, form);

  useEffect(() => {
    seedFormFromStore(form, fieldName, binding.value, false);
    if (binding.value == null) {
      binding.sync(false);
      binding.onBlur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Controller
      name={fieldName}
      control={form.control}
      render={({ field: { onChange, value, ...field }, fieldState, formState }) => {
        const showError = fieldState.error && (formState.isSubmitted || fieldState.isTouched);

        return (
          <FormControl
            error={!!showError}
            sx={{ alignItems: 'flex-start' }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  onChange={(_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
                    trackEvent(`${component.attribute}_CHECKED`);
                    onChange(checked);
                    binding.sync(checked);
                    binding.onBlur();
                    void form.trigger(fieldName);
                  }}
                  value={!!value}
                  checked={!!value}
                  color={showError ? 'error' : undefined}
                />
              }
              label={
                <>
                  <RichText
                    spans={component.spans}
                    variant={component.variant}
                  />
                  {showError && <FormHelperText sx={{ marginLeft: '0px' }}>{fieldState.error?.message}</FormHelperText>}
                </>
              }
              sx={{
                alignItems: component.align_items === 'center' ? 'center' : 'flex-start',
                gap: 1,
              }}
            />
          </FormControl>
        );
      }}
    />
  );
}
